/**
 * Continuity + 64px proportion quality gates (no Phaser).
 * SSOT: docs/testing/Game_Proto_64px_캐릭터_품질게이트.md
 */

export type ContinuityReport = {
  spriteId: string;
  heightE: number;
  heightN: number;
  heightS: number;
  solidE: number;
  solidN: number;
  solidS: number;
  heightOk: boolean;
  massOk: boolean;
  wIsFlipE: boolean;
};

export type ProportionReport = {
  bboxHeight: number;
  bboxWidth: number;
  headHeight: number;
  solidCount: number;
  uniqueOpaqueKeys: number;
  legOccupiedCols: number;
  maxLegRun: number;
  legGap: number;
  maxArmRun: number;
  bboxOk: boolean;
  headOk: boolean;
  solidOk: boolean;
  legsOk: boolean;
  shadingOk: boolean;
  ok: boolean;
  failReasons: string[];
};

export function frameHeight(rows: string[]): number {
  let min = rows.length;
  let max = -1;
  for (let y = 0; y < rows.length; y++) {
    if (/[^.]/.test(rows[y]!)) {
      min = Math.min(min, y);
      max = Math.max(max, y);
    }
  }
  return max < 0 ? 0 : max - min + 1;
}

export function solidCount(rows: string[]): number {
  return rows.join("").replace(/\./g, "").length;
}

function bbox(rows: string[]): {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
} {
  let minX = 64;
  let maxX = -1;
  let minY = 64;
  let maxY = -1;
  for (let y = 0; y < rows.length; y++) {
    const r = rows[y]!;
    for (let x = 0; x < r.length; x++) {
      if (r[x] === ".") continue;
      minX = Math.min(minX, x);
      maxX = Math.max(maxX, x);
      minY = Math.min(minY, y);
      maxY = Math.max(maxY, y);
    }
  }
  if (maxX < 0) return { minX: 0, maxX: 0, minY: 0, maxY: 0 };
  return { minX, maxX, minY, maxY };
}

/** Contiguous solid-column runs within [y0,y1] ∩ [x0,x1]. */
function columnRuns(
  rows: string[],
  y0: number,
  y1: number,
  x0: number,
  x1: number,
): number[] {
  const occupied: boolean[] = [];
  for (let x = x0; x <= x1; x++) {
    let hit = false;
    for (let y = y0; y <= y1; y++) {
      const ch = rows[y]?.[x];
      if (ch && ch !== ".") {
        hit = true;
        break;
      }
    }
    occupied.push(hit);
  }
  const runs: number[] = [];
  let run = 0;
  for (const o of occupied) {
    if (o) run += 1;
    else if (run > 0) {
      runs.push(run);
      run = 0;
    }
  }
  if (run > 0) runs.push(run);
  return runs;
}

/**
 * Proportion gates for a single idle (or any) frame — typically dirs.e.idle[0].
 */
export function measureProportionQuality(rows: string[]): ProportionReport {
  const b = bbox(rows);
  const bboxHeight = b.maxY - b.minY + 1;
  const bboxWidth = b.maxX - b.minX + 1;
  const solid = solidCount(rows);
  const keySet = new Set<string>();
  for (const row of rows) {
    for (const ch of row) {
      if (ch !== ".") keySet.add(ch);
    }
  }
  const uniqueOpaqueKeys = keySet.size;

  const headBandEnd = b.minY + Math.max(1, Math.floor(bboxHeight * 0.32));
  let headMin = 64;
  let headMax = -1;
  for (let y = b.minY; y <= Math.min(b.maxY, headBandEnd); y++) {
    if (/[^.]/.test(rows[y]!)) {
      headMin = Math.min(headMin, y);
      headMax = Math.max(headMax, y);
    }
  }
  const headHeight = headMax < 0 ? 0 : headMax - headMin + 1;

  const legTop = b.minY + Math.floor(bboxHeight * 0.6);
  const legRuns = columnRuns(rows, legTop, b.maxY, b.minX, b.maxX);
  const legOccupiedCols = legRuns.reduce((a, n) => a + n, 0);
  const maxLegRun = legRuns.length ? Math.max(...legRuns) : 0;
  const legGap =
    legRuns.length >= 2
      ? (() => {
          // Gap estimate: occupied span minus occupied cols
          let first = -1;
          let last = -1;
          for (let x = b.minX; x <= b.maxX; x++) {
            let hit = false;
            for (let y = legTop; y <= b.maxY; y++) {
              if (rows[y]?.[x] && rows[y]![x] !== ".") {
                hit = true;
                break;
              }
            }
            if (hit) {
              if (first < 0) first = x;
              last = x;
            }
          }
          if (first < 0) return 0;
          return last - first + 1 - legOccupiedCols;
        })()
      : 0;

  const armTop = b.minY + Math.floor(bboxHeight * 0.28);
  const armBot = b.minY + Math.floor(bboxHeight * 0.55);
  const armRuns = columnRuns(rows, armTop, armBot, b.minX, b.maxX);
  // Soft: widest run that isn't the fat torso middle — use max run as arm proxy
  // when multiple runs; if single blob, maxArmRun = that run (will fail if thick).
  const maxArmRun = armRuns.length ? Math.max(...armRuns) : 0;

  const bboxOk =
    bboxHeight >= 44 &&
    bboxHeight <= 54 &&
    bboxWidth >= 18 &&
    bboxWidth <= 34;
  const headOk = headHeight >= 12 && headHeight <= 16;
  const solidOk = solid >= 420 && solid <= 900;
  const legsOk =
    legOccupiedCols <= 14 && maxLegRun <= 6 && legGap >= 1 && legRuns.length >= 2;
  /** Duelyst bar: multi-ramp — flat 3–4 key outline blocks fail. */
  const shadingOk = uniqueOpaqueKeys >= 6;

  const failReasons: string[] = [];
  if (!bboxOk) {
    failReasons.push(`bbox ${bboxWidth}x${bboxHeight} not in 18–34 × 44–54`);
  }
  if (!headOk) failReasons.push(`headHeight ${headHeight} not in 12–16`);
  if (!solidOk) failReasons.push(`solidCount ${solid} not in 420–900`);
  if (!legsOk) {
    failReasons.push(
      `legs cols=${legOccupiedCols} maxRun=${maxLegRun} gap=${legGap} (need cols≤14 run≤6 gap≥1, 2+ runs)`,
    );
  }
  if (!shadingOk) {
    failReasons.push(
      `uniqueOpaqueKeys ${uniqueOpaqueKeys} < 6 (need Duelyst-style multi-ramp; not outline-only)`,
    );
  }

  return {
    bboxHeight,
    bboxWidth,
    headHeight,
    solidCount: solid,
    uniqueOpaqueKeys,
    legOccupiedCols,
    maxLegRun,
    legGap,
    maxArmRun,
    bboxOk,
    headOk,
    solidOk,
    legsOk,
    shadingOk,
    ok: bboxOk && headOk && solidOk && legsOk && shadingOk,
    failReasons,
  };
}

export function measureDirContinuity(
  spriteId: string,
  dirs: {
    e: string[];
    n: string[];
    s: string[];
    w: string[];
  },
): ContinuityReport {
  const he = frameHeight(dirs.e);
  const hn = frameHeight(dirs.n);
  const hs = frameHeight(dirs.s);
  const se = solidCount(dirs.e);
  const sn = solidCount(dirs.n);
  const ss = solidCount(dirs.s);
  const e0 = dirs.e[0] ?? "";
  const w0 = dirs.w[0] ?? "";
  const flipped = [...e0].reverse().join("");
  const spread = Math.max(se, sn, ss) - Math.min(se, sn, ss);
  return {
    spriteId,
    heightE: he,
    heightN: hn,
    heightS: hs,
    solidE: se,
    solidN: sn,
    solidS: ss,
    heightOk: Math.abs(he - hn) <= 4 && Math.abs(he - hs) <= 4,
    massOk: spread <= 72,
    wIsFlipE: w0 === flipped,
  };
}
